import { Sentiment as _Sentiment } from "@clockwise/design-system";
import { Close, SvgIconComponent } from "@clockwise/icons";
import classNames from "classnames";
import { noop } from "lodash";
import React from "react";
import { TradeoffAttendee } from "../chat/ai-chat/utils/types";
import { ECAttendeeStack } from "../event-card/atoms/ECAttendeeStack";

type Variant = "solid" | "outlined";
type Sentiment = Extract<_Sentiment, "neutral" | "positive">;

type Profile = {
  __typename: "UserProfile";
  givenName: string | null;
  familyName: string | null;
  externalImageUrl: string | null;
} | null;

export type AvatarChipPerson = {
  id: string;
  primaryEmail: string;
  isYou: boolean;
  profile: Profile;
};

const COLOR_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "cw-bg-neutral-inset cw-text-default",
    positive: "cw-bg-positive cw-text-positive",
  },
  outlined: {
    // empahsis outline currenlty not specced so it's the same as solid
    neutral: "cw-bg-neutral cw-text-default cw-border cw-border-solid cw-border-muted",
    positive: "cw-bg-positive cw-text-positive cw-border cw-border-solid cw-border-positive",
  },
};

const HOVER_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "hover:cw-bg-neutral-inset-hover cw-cursor-pointer",
    positive: "hover:cw-bg-positive-hover cw-cursor-pointer",
  },
  outlined: {
    neutral: "hover:cw-bg-neutral-hover cw-cursor-pointer",
    positive: "hover:cw-bg-positive-hover cw-cursor-pointer",
  },
};

const DELETE_ICON_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "cw-text-subtle",
    positive: "cw-text-positive",
  },
  outlined: {
    neutral: "cw-text-subtle",
    positive: "cw-text-positive",
  },
};

const ICON_COLOR_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "cw-text-default",
    positive: "cw-text-positive",
  },
  outlined: {
    neutral: "cw-text-default",
    positive: "cw-text-positive",
  },
};

const DISABLED_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "cw-opacity-70 cw-pointer-events-none",
    positive: "cw-opacity-75 cw-pointer-events-none",
  },
  outlined: {
    neutral: "cw-opacity-70 cw-pointer-events-none",
    positive: "cw-opacity-75 cw-pointer-events-none",
  },
};

// Leading text must vary for accessibility
const LEADING_TEXT_CLASSES_HASH: { [v in Variant]: { [s in Sentiment]: string } } = {
  solid: {
    neutral: "cw-text-muted",
    positive: "cw-text-muted",
  },
  outlined: {
    neutral: "cw-text-subtle",
    positive: "cw-text-muted",
  },
};

const Avatars = ({ persons }: { persons: AvatarChipPerson[] }) => {
  const profilesAsAttendees: TradeoffAttendee[] = persons.map((person) => {
    return {
      __typename: "OrgPerson",
      id: person.id,
      isYou: person.isYou,
      profile: person.profile,
      userId: null,
      primaryCalendarId: person.primaryEmail,
    };
  });
  return <ECAttendeeStack attendees={profilesAsAttendees} maxShown={4} />;
};

export const AvatarChip = ({
  onClick,
  sentiment,
  variant,
  persons,
  disabled = false,
  onDelete,
  endIcon: EndIconComponent,
  leadingText,
}: {
  sentiment: Sentiment;
  variant: Variant;
  persons: AvatarChipPerson[];
  disabled?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  endIcon?: SvgIconComponent;
  leadingText?: string;
}) => {
  const showDelete = !!onDelete;
  const hasOnClickHander = !!onClick && !disabled;

  const hoverClasses = HOVER_CLASSES_HASH[variant][sentiment];
  const colorClasses = COLOR_CLASSES_HASH[variant][sentiment];

  const disabledClasses = disabled ? DISABLED_CLASSES_HASH[variant][sentiment] : "";
  const deleteIconColorClasses = DELETE_ICON_CLASSES_HASH[variant][sentiment];
  const iconColorClasses = ICON_COLOR_CLASSES_HASH[variant][sentiment];
  const leadingTextColorClasses = LEADING_TEXT_CLASSES_HASH[variant][sentiment];

  const EndIcon = EndIconComponent ? (
    <EndIconComponent className={classNames(iconColorClasses, "cw-w-4 cw-h-4")} aria-hidden />
  ) : null;

  const multipleAttendees = persons.length > 1;

  const getName = (person: AvatarChipPerson) => {
    const email = person.primaryEmail;
    const profile = person.profile;

    if (!!profile && profile.familyName && profile.givenName) {
      return `${profile.givenName} ${profile.familyName}`;
    }
    // If profile or aspects of profile are null, defaul to email
    return email;
  };
  const label = multipleAttendees ? `${persons.length} people` : getName(persons[0]);

  return (
    <div
      className={classNames(
        "cw-flex cw-rounded-full cw-caption cw-items-center",
        colorClasses,
        disabledClasses,
      )}
      aria-disabled={disabled}
    >
      <div
        tabIndex={hasOnClickHander ? 0 : -1}
        onKeyDown={(e) => {
          if (e.key === "Enter" && hasOnClickHander) {
            onClick();
          }
        }}
        onClick={hasOnClickHander ? onClick : noop}
        className={classNames("cw-flex cw-rounded-l-full cw-items-center cw-pl-0.5 cw-py-0.5", {
          "cw-rounded-r-full cw-pr-1": !showDelete,
          [hoverClasses]: hasOnClickHander,
        })}
      >
        {leadingText && (
          <div className={classNames(leadingTextColorClasses, "cw-px-1")}>{leadingText} </div>
        )}
        <Avatars persons={persons} />
        <div className="cw-mx-1"> {label} </div>
        {EndIconComponent && <div className="cw-flex cw-items-center cw-pr-1"> {EndIcon}</div>}
      </div>

      {!!showDelete && (
        <div
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !disabled) {
              onDelete();
            }
          }}
          onClick={!disabled ? onDelete : noop}
          className={classNames(
            "cw-flex cw-items-center cw-rounded-r-full cw-pr-2 cw-pl-0.5 cw-py-0.5 cw-h-full",
            hoverClasses,
          )}
        >
          <Close className={classNames("cw-w-[14px] cw-h-[14px]", deleteIconColorClasses)} />
        </div>
      )}
    </div>
  );
};
